import { dateLocal, dateToLocaleDateString } from '../../cross-app-shared/services/dates-and-durations.ts';
import type { Email, Uid } from '../../cross-app-shared/types/common';
import type { User } from '../../compliance-shared/types/common';
import type { ISO8601Timestamp, Role } from '../../compliance-shared/types/compliance';
import { computed, ref, type Ref, type ComputedRef } from 'vue';
import { UNIT_TEST, INTEGRATION_TEST } from '../../cross-app-shared/services/automated-testing.ts';
import { crossAppActions } from '../../cross-app-frontend/cross-app-actions';
import { userConfirmedLoggedInPromise } from '../../cross-app-frontend/firebase';
import { tenantID } from '@/router/route-constants';

export const usersRef = ref<User[]>([]);
export const usersByUidRef = ref<Map<Uid, User>>(new Map());

if (UNIT_TEST || INTEGRATION_TEST) {
  const user = {
    uid: 'testtesttesttestesttesttestt' as Uid,
    email: 'test@example.com' as Email,
    name: 'Test User',
    facilityName: 'Test Facility',
    primaryFacilityID: null,
  };
  usersRef.value = [user];
  usersByUidRef.value.set(user.uid, user);
} else {
  userConfirmedLoggedInPromise.then(() =>
    crossAppActions.users.listForTenant(tenantID).then((users) => {
      usersRef.value = users;
      const map = new Map<Uid, User>();
      for (const user of users) {
        map.set(user.uid, user);
      }
      usersByUidRef.value = map;
    }),
  );
}

export function getUserDisplayName(user: User): string {
  return user.name || user.email || 'Unknown User';
}

export function compareUsersByUid(a: Uid, b: Uid): number {
  const aUser = usersByUidRef.value.get(a);
  const bUser = usersByUidRef.value.get(b);

  if (aUser && bUser) {
    return getUserDisplayName(aUser).localeCompare(getUserDisplayName(bUser));
  } else if (aUser) {
    return -1;
  } else if (bUser) {
    return 1;
  } else {
    return 0;
  }
}

export function formatUid(ref: Ref<Uid | 'system' | null | undefined>): ComputedRef<string> {
  return computed(() => {
    const uid = ref.value;
    if (!uid) {
      return 'someone';
    }

    if (uid === 'system') {
      return 'System';
    }

    const user = usersByUidRef.value.get(uid);
    return user?.name || user?.email || 'someone';
  });
}

export function formatUidWithDate(
  uidRef: Ref<Uid | 'system' | null | undefined>,
  dateRef: Ref<ISO8601Timestamp | null | undefined>,
  systemActionDescription = 'automatically',
): ComputedRef<string> {
  return computed(() => {
    let result = '';
    if (dateRef.value) {
      result += dateToLocaleDateString(dateLocal(dateRef.value));
      const userWhoCreated = usersByUidRef.value.get(uidRef.value as Uid);
      if (userWhoCreated) {
        result += ' by ' + (userWhoCreated.name || userWhoCreated.email);
      } else if (uidRef.value === 'system') {
        result += ' ' + systemActionDescription;
      }
    }
    return result;
  });
}

export function formatUids(
  ref: Ref<Uid[]>,
  exclude: Ref<Uid | null | undefined>,
  emptyValue: string,
): ComputedRef<string> {
  return computed(() => {
    const pieces = ref.value
      .filter((uid) => uid !== exclude.value)
      .map((uid) => {
        const user = usersByUidRef.value.get(uid);
        return user?.name || user?.email || 'someone';
      });

    if (pieces.length === 0) {
      return emptyValue;
    }

    return pieces.join(', ');
  });
}

export function uidOrRoleIDToString(roles: Role[] | null, uidOrRoleID: Uid | Role['roleID']) {
  const user = usersByUidRef.value.get(uidOrRoleID as Uid);
  const userName = user?.name || user?.email || uidOrRoleID;

  if (uidOrRoleID.startsWith('role_')) {
    const role = roles?.find((r) => r.roleID === uidOrRoleID);
    if (role) {
      return role.name;
    }
  }

  return userName;
}
